/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  PlusOutlined,
  MinusOutlined,
  AppstoreOutlined,
  MenuOutlined,
} from '@ant-design/icons'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'

import useRouter from '../../../hooks/useRouter'
import useTranslations from '../../../hooks/useTranslations'
import ANTDButton from '../../../shared/antd/ANTDButton'
import ANTDCard from '../../../shared/antd/ANTDCard'
import ANTDSpin from '../../../shared/antd/ANTDSpin'
import { apiParams } from '../../../utils'
import { isEqual, values } from '../../../utils/javascript'
import {
  getScrapMaterialsApi,
  getScrapListOnMaterialTypeApi,
  getInfoTillJobApi,
  getConnectedJobsApi,
} from '../inventory.api'
import '../inventory.scss'
import {
  getJobsFields,
  getPartFields,
  getVehicleFields,
  plasticItems,
  steelItems,
  traceabilityJobTitles,
  getTraceabilityFieldsJobType,
} from '../inventory.description'

const FIXED_LEVELS = ['vehicleDetails', 'materialList', 'partList']
const getPartSalesChildren = (selectedItems = {}, allJobsList = {}) =>
  allJobsList[selectedItems.PART_SALES_PRODUCER]?.map(job => job.jobType) || []

const getFixedJobLevels = (
  materialTypeKey,
  selectedItems = {},
  allJobsList = {},
) => {
  const partSalesChildren = getPartSalesChildren(selectedItems, allJobsList)
  const hasLegacyDirectMaterialSales = partSalesChildren.includes(
    'MATERIAL_SALES_PART_PRODUCER',
  )
  const hasLegacyPartProduction = partSalesChildren.includes('PART_PRODUCTION')

  if (materialTypeKey !== 'job_Plastic') {
    if (hasLegacyPartProduction) {
      return ['PART_SALES_PRODUCER', 'PART_PRODUCTION']
    }

    return hasLegacyDirectMaterialSales
      ? ['PART_SALES_PRODUCER', 'MATERIAL_SALES_PART_PRODUCER']
      : ['PART_SALES_PRODUCER']
  }

  return hasLegacyDirectMaterialSales
    ? ['PART_SALES_PRODUCER', 'MATERIAL_SALES_PART_PRODUCER']
    : ['PART_SALES_PRODUCER', 'PART_PRODUCTION', 'MATERIAL_SALES_PART_PRODUCER']
}

const buildDynamicJobLevels = (
  materialTypeKey,
  selectedItems,
  allJobsList,
  fixedJobLevels,
) => {
  const canonicalItems =
    materialTypeKey === 'job_Plastic' ? plasticItems : steelItems
  const initialParentLevel = fixedJobLevels[fixedJobLevels.length - 1]
  const canonicalStartIndex = canonicalItems.includes(initialParentLevel)
    ? canonicalItems.indexOf(initialParentLevel) + 1
    : 0
  const canonical = canonicalItems.slice(canonicalStartIndex)
  const result = []
  let parentJobId = initialParentLevel
    ? selectedItems[initialParentLevel]
    : null

  for (let i = 0; i < canonical.length; i++) {
    const jobType = canonical[i]

    if (parentJobId && allJobsList[parentJobId]?.length) {
      const childTypes = new Set(
        allJobsList[parentJobId].map(job => job.jobType),
      )
      const availableTypes = canonical
        .slice(i)
        .filter(type => childTypes.has(type))

      if (!availableTypes.length) {
        break
      }

      const nextType = availableTypes[0]
      if (nextType !== jobType) {
        i = canonical.indexOf(nextType) - 1
        continue
      }
    }

    result.push(jobType)

    if (selectedItems[jobType]) {
      parentJobId = selectedItems[jobType]
    } else if (parentJobId && allJobsList[parentJobId]?.length) {
      const childTypes = new Set(
        allJobsList[parentJobId].map(job => job.jobType),
      )
      const remaining = canonical
        .slice(i + 1)
        .filter(type => childTypes.has(type))
      return [...result, ...remaining]
    } else {
      return [...result, ...canonical.slice(i + 1)]
    }
  }

  return result
}

const VehicleTraceability = ({ vehicleData: propVehicleData, type }) => {
  const { location } = useRouter()
  const { t } = useTranslations()
  const vehicleData = propVehicleData || location?.state || {}

  const [selectedItems, setSelectedItems] = useState({})
  const [materials, setMaterials] = useState([])
  const [partsByMaterial, setPartsByMaterial] = useState({})
  const [partProducersByPart, setPartProducersByPart] = useState({})
  const [partProductionJob, setPartProductionJob] = useState({})
  const [allJobsList, setAllJobsList] = useState({})
  const [jobsPagination, setJobsPagination] = useState({})
  const [loading, setLoading] = useState(false)
  const [viewType, setViewType] = useState('horizontal') // 'horizontal' or 'vertical'
  const [columnOffsets, setColumnOffsets] = useState({})
  const [totalProcessLoss, setTotalProcessLoss] = useState(0)
  const selectedMaterial = materials.find(
    material => material.id === selectedItems.materialList,
  )

  const fixedJobLevels = useMemo(
    () =>
      getFixedJobLevels(
        selectedMaterial?.materialTypeKey,
        selectedItems,
        allJobsList,
      ),
    [selectedMaterial?.materialTypeKey, selectedItems, allJobsList],
  )

  const dynamicJobLevels = useMemo(
    () =>
      buildDynamicJobLevels(
        selectedMaterial?.materialTypeKey,
        selectedItems,
        allJobsList,
        fixedJobLevels,
      ),
    [
      selectedMaterial?.materialTypeKey,
      selectedItems,
      allJobsList,
      fixedJobLevels,
    ],
  )

  const levelOrder = useMemo(
    () => [...FIXED_LEVELS, ...fixedJobLevels, ...dynamicJobLevels],
    [dynamicJobLevels, fixedJobLevels],
  )

  // Common function to render key-value pairs
  const renderKeyValueFields = fields => {
    return (
      <div>
        {fields.map((field, index) => (
          <div key={index}>
            {field?.isLink ? (
              <>
                <strong>{t(field.translationKey)} : </strong>
                <Link
                  to={`/jobs?jobId=${field.jobId}&jobType=${field.jobType}&view=true`}
                  className="color-link"
                  style={{ cursor: 'pointer' }}
                >
                  {field.value}
                </Link>
              </>
            ) : (
              <>
                <strong>{t(field.translationKey)} :</strong>{' '}
                {field.value || '-'}
              </>
            )}
          </div>
        ))}
      </div>
    )
  }

  const mapJobsToCards = useCallback(
    ({ jobs, jobType, level }) =>
      jobs.map(job => ({
        id: job.jobId,
        content: renderKeyValueFields(
          getJobsFields({
            jobType: getTraceabilityFieldsJobType(jobType),
            jobData: job,
            materialType: selectedMaterial?.materialTypeKey,
            t,
            type,
          }),
        ),
        level,
        title: traceabilityJobTitles[jobType],
      })),
    [selectedMaterial?.materialTypeKey, t, type],
  )

  const getParentJobId = useCallback(
    jobType => {
      const dynamicIndex = dynamicJobLevels.indexOf(jobType)
      if (dynamicIndex >= 0) {
        const parentLevel =
          dynamicIndex === 0
            ? fixedJobLevels[fixedJobLevels.length - 1]
            : dynamicJobLevels[dynamicIndex - 1]
        return parentLevel ? selectedItems[parentLevel] : null
      }

      if (jobType === 'PART_SALES_PRODUCER') {
        return null
      }
      if (jobType === 'PART_PRODUCTION') {
        return selectedItems.PART_SALES_PRODUCER
      }
      if (jobType === 'MATERIAL_SALES_PART_PRODUCER') {
        return (
          selectedItems.PART_PRODUCTION || selectedItems.PART_SALES_PRODUCER
        )
      }
      if (jobType === 'RECYCLE') {
        return selectedMaterial?.materialTypeKey === 'job_Plastic'
          ? selectedItems.SORTING
          : selectedItems.MATERIAL_PRODUCTION
      }
      return null
    },
    [
      selectedItems,
      dynamicJobLevels,
      fixedJobLevels,
      selectedMaterial?.materialTypeKey,
    ],
  )

  const getJobsForLevel = useCallback(
    (jobType, parentJobId) => {
      if (!parentJobId) {
        return []
      }
      return (allJobsList[parentJobId] || []).filter(
        job => job.jobType === jobType,
      )
    },
    [allJobsList],
  )

  const syncPartProductionJobs = useCallback(
    (partId, jobs = []) => {
      if (!partId) {
        return
      }
      const partProductionJobs = jobs.filter(
        job => job?.jobType === 'PART_PRODUCTION',
      )

      if (!partProductionJobs.length) {
        return
      }

      setPartProductionJob(prev => ({
        ...prev,
        [partId]: partProductionJobs,
      }))
    },
    [setPartProductionJob],
  )

  const fetchConnectedJobsForJobId = useCallback(
    async (jobId, options = {}) => {
      const { sourceLevel, partId } = options

      if (!jobId) {
        return
      }

      if (allJobsList[jobId]) {
        if (sourceLevel === 'PART_SALES_PRODUCER') {
          syncPartProductionJobs(partId, allJobsList[jobId] || [])
        }
        return
      }

      setLoading(true)
      try {
        const response = await getConnectedJobsApi({ jobId, page: 1 })
        const jobs = response?.data?.list || []
        if (jobs.length) {
          setAllJobsList(prev => ({
            ...prev,
            [jobId]: jobs,
          }))
          if (sourceLevel === 'PART_SALES_PRODUCER') {
            syncPartProductionJobs(partId, jobs)
          }
          setJobsPagination(prev => ({
            ...prev,
            [jobId]: {
              page: 1,
              hasMore: 1 < (response.data.lastPage || 1),
              loading: false,
            },
          }))
        } else {
          setAllJobsList(prev => ({ ...prev, [jobId]: [] }))
        }
      } catch (error) {
        setAllJobsList(prev => ({ ...prev, [jobId]: [] }))
      } finally {
        setLoading(false)
      }
    },
    [allJobsList, syncPartProductionJobs],
  )

  // Load downstream jobs when part production (or material sales) is selected
  useEffect(() => {
    if (
      selectedMaterial?.materialTypeKey === 'job_Plastic' &&
      selectedItems.PART_PRODUCTION
    ) {
      fetchConnectedJobsForJobId(selectedItems.PART_PRODUCTION)
    }
  }, [
    selectedItems.PART_PRODUCTION,
    fetchConnectedJobsForJobId,
    selectedMaterial?.materialTypeKey,
  ])

  useEffect(() => {
    if (
      selectedMaterial?.materialTypeKey === 'job_Plastic' &&
      selectedItems.MATERIAL_SALES_PART_PRODUCER
    ) {
      fetchConnectedJobsForJobId(selectedItems.MATERIAL_SALES_PART_PRODUCER)
    }
  }, [
    selectedItems.MATERIAL_SALES_PART_PRODUCER,
    fetchConnectedJobsForJobId,
    selectedMaterial?.materialTypeKey,
  ])

  // Align each column with the selected card in the previous column (horizontal view)
  useEffect(() => {
    if (viewType !== 'horizontal') {
      setColumnOffsets({})
      return
    }

    const COLUMN_HEADER_OFFSET = 64

    const updateColumnOffsets = () => {
      const newOffsets = {}

      for (let i = 0; i < levelOrder.length - 1; i++) {
        const level = levelOrder[i]
        const childLevel = levelOrder[i + 1]
        const selectedId = selectedItems[level]

        if (selectedId == null) {
          continue
        }

        const cardWrapper = document.querySelector(
          `[data-trace-level="${level}"][data-trace-id="${selectedId}"]`,
        )

        if (!cardWrapper) {
          continue
        }

        newOffsets[childLevel] = cardWrapper.offsetTop - COLUMN_HEADER_OFFSET
      }

      setColumnOffsets(newOffsets)
    }

    const frameId = requestAnimationFrame(updateColumnOffsets)
    return () => cancelAnimationFrame(frameId)
  }, [
    viewType,
    levelOrder,
    selectedItems,
    dynamicJobLevels,
    allJobsList,
    loading,
  ])

  // Fetch scrap materials from API
  useEffect(() => {
    const fetchScrapMaterials = async () => {
      // Get scrapId from vehicleData - could be id, scrapId, or systemId
      const scrapId =
        vehicleData?.id || vehicleData?.scrapId || vehicleData?.systemId
      setLoading(true)
      try {
        const params = apiParams({
          params: {
            scrapId,
            jobType: 'VEHICLE_PRODUCTION',
            scrapType: 'PART',
          },
        })
        const response = await getScrapMaterialsApi({ params })

        // Transform API response to match component's expected format
        if (response?.data?.list || Array.isArray(response?.data)) {
          const materialsList = response?.data?.list || response?.data
          const transformedMaterials = materialsList.map((item, index) => ({
            id: item?.materialTypeId,
            name: item?.materialTypeKey || item?.materialType || '-',
            recycledPercent:
              item?.recycledPercent || item?.recycledMaterialPercentage || 0,
            weight: item?.materialWeight,
            ...item, // Include all other fields from API response
          }))
          setMaterials(transformedMaterials)
        } else {
          // If response format is different, use the data as is or fallback
          setMaterials([])
        }
      } catch (error) {
        // Error handling - set empty materials on error
        setMaterials([])
      } finally {
        setLoading(false)
      }
      setLoading(false)
    }

    fetchScrapMaterials()
  }, [vehicleData])

  const handleCardClick = async (level, id, event) => {
    const isDeselecting = selectedItems[level] === id

    setSelectedItems(prev => {
      const newState = { ...prev }

      // Set the selected item for this level
      newState[level] = newState[level] === id ? null : id
      // Clear all child levels when a parent is deselected or changed
      const currentLevelIndex = levelOrder.indexOf(level)
      // If deselecting, clear all children
      if (newState[level] === null) {
        for (let i = currentLevelIndex + 1; i < levelOrder.length; i++) {
          newState[levelOrder[i]] = null
        }
      } else {
        // If selecting, clear only deeper levels
        for (let i = currentLevelIndex + 1; i < levelOrder.length; i++) {
          newState[levelOrder[i]] = null
        }
      }

      return newState
    })

    // Clear data for child levels when deselecting
    if (isDeselecting) {
      const currentLevelIndex = levelOrder.indexOf(level)

      // Recursively collect all descendant job IDs to clear
      const collectDescendantJobIds = (parentJobId, collected = new Set()) => {
        if (!parentJobId || collected.has(parentJobId)) return collected
        collected.add(parentJobId)

        const childJobs = allJobsList[parentJobId]
        if (Array.isArray(childJobs)) {
          childJobs.forEach(job => {
            if (job?.jobId) {
              collectDescendantJobIds(job.jobId, collected)
            }
          })
        }
        return collected
      }

      // Get all job IDs that need to be cleared
      const jobIdsToClear = new Set()

      // Collect job IDs from current selection path that are children of this level
      for (let i = currentLevelIndex + 1; i < levelOrder.length; i++) {
        const childLevel = levelOrder[i]
        const childId = selectedItems[childLevel]
        if (childId) {
          jobIdsToClear.add(childId)
          // Recursively collect all descendants of this child
          collectDescendantJobIds(childId, jobIdsToClear)
        }
      }

      // Also add the current id if it's a job level and recursively collect its descendants
      if (
        id &&
        level !== 'vehicleDetails' &&
        level !== 'materialList' &&
        level !== 'partList'
      ) {
        collectDescendantJobIds(id, jobIdsToClear)
      }

      // Clear allJobsList entries
      if (jobIdsToClear.size > 0) {
        setAllJobsList(prev => {
          const newList = { ...prev }
          jobIdsToClear.forEach(jobId => {
            delete newList[jobId]
          })
          return newList
        })

        // Clear jobsPagination entries
        setJobsPagination(prev => {
          const newPagination = { ...prev }
          jobIdsToClear.forEach(jobId => {
            delete newPagination[jobId]
          })
          return newPagination
        })
      }

      // Clear partProductionJob when partList is deselected
      if (level === 'partList' && id) {
        setPartProductionJob(prev => {
          const newPartProductionJob = { ...prev }
          delete newPartProductionJob[id]
          return newPartProductionJob
        })
        setPartProducersByPart(prev => {
          const newPartProducers = { ...prev }
          delete newPartProducers[id]
          return newPartProducers
        })
      }

      // Clear partsByMaterial when materialList is deselected
      if (level === 'materialList' && id) {
        setPartsByMaterial(prev => {
          const newPartsByMaterial = { ...prev }
          delete newPartsByMaterial[id]
          return newPartsByMaterial
        })
      }
    }

    // Fetch parts when a material is clicked
    if (level === 'materialList' && id) {
      const materialId = id
      const scrapId =
        vehicleData?.id || vehicleData?.scrapId || vehicleData?.systemId
      setLoading(true)
      if (materialId && scrapId && !partsByMaterial[materialId]) {
        try {
          const params = apiParams({
            params: {
              scrapId,
              jobType: 'VEHICLE_PRODUCTION',
              materialTypeId: materialId,
            },
          })
          const response = await getScrapListOnMaterialTypeApi({ params })

          if (response?.data?.list || Array.isArray(response?.data)) {
            const partsList = response?.data?.list || []
            setPartsByMaterial(prev => ({
              ...prev,
              [materialId]: partsList,
            }))
          }
        } catch (error) {
          setPartsByMaterial(prev => ({
            ...prev,
            [materialId]: [],
          }))
        } finally {
          setLoading(false)
        }
      }
      setLoading(false)
    }

    // Fetch part producers when a part is clicked
    if (level === 'partList' && id) {
      if (id && !partProducersByPart[id]) {
        setLoading(true)
        try {
          const params = apiParams({
            params: {
              scrapId: id,
              fromJobType: 'VEHICLE_PRODUCTION',
              toJobType: 'PART_PRODUCTION',
            },
          })
          const response = await getInfoTillJobApi({ params })

          if (response?.data?.list?.length) {
            const producersList = response?.data?.list || []
            const filteredProducersList = producersList?.filter(
              item => item?.jobType === 'PART_SALES_PRODUCER',
            )
            const vehicleProductionJob = producersList?.filter(
              item => item?.jobType === 'PART_PRODUCTION',
            )
            setPartProducersByPart(prev => ({
              ...prev,
              [id]: filteredProducersList,
            }))
            setPartProductionJob(prev => ({
              ...prev,
              [id]: vehicleProductionJob,
            }))
          }
        } catch (error) {
          setPartProducersByPart(prev => ({
            ...prev,
            [id]: [],
          }))
        } finally {
          setLoading(false)
        }
      }
      setLoading(false)
    }

    // Fetch connected jobs for selectable job cards that still need downstream nodes
    const levelsWithoutConnectedJobsFetch = [...FIXED_LEVELS, 'RECOVERY']
    const shouldFetchConnectedJobs =
      !levelsWithoutConnectedJobsFetch.includes(level)

    if (shouldFetchConnectedJobs && !isDeselecting) {
      await fetchConnectedJobsForJobId(id, {
        sourceLevel: level,
        partId: selectedItems.partList || (level === 'partList' ? id : null),
      })
    }
  }

  const fetchMoreJobs = async jobId => {
    const pagination = jobsPagination[jobId]
    if (!pagination || !pagination.hasMore || pagination.loading) return

    setJobsPagination(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], loading: true },
    }))

    try {
      const nextPage = pagination.page + 1
      const response = await getConnectedJobsApi({
        jobId,
        page: nextPage,
      })

      if (response?.data?.list) {
        setAllJobsList(prev => ({
          ...prev,
          [jobId]: [...(prev[jobId] || []), ...response.data.list],
        }))

        setJobsPagination(prev => ({
          ...prev,
          [jobId]: {
            page: nextPage,
            hasMore: nextPage < (response.data.lastPage || 1),
            loading: false,
          },
        }))
      } else {
        setJobsPagination(prev => ({
          ...prev,
          [jobId]: { ...prev[jobId], hasMore: false, loading: false },
        }))
      }
    } catch (error) {
      setJobsPagination(prev => ({
        ...prev,
        [jobId]: { ...prev[jobId], loading: false },
      }))
    }
  }

  const handleScroll = async (e, level) => {
    if (level === 'RECOVERY') {
      const parentId = selectedItems.RECYCLE
      if (!parentId) return

      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        const pagination = jobsPagination[parentId]
        if (pagination?.hasMore && !pagination?.loading) {
          setJobsPagination(prev => ({
            ...prev,
            [parentId]: { ...prev[parentId], loading: true },
          }))

          try {
            const nextPage = pagination.page + 1
            const response = await getConnectedJobsApi({
              jobId: parentId,
              page: nextPage,
            })

            if (response) {
              const newJobs = response?.data?.list || []
              setAllJobsList(prev => ({
                ...prev,
                [parentId]: [...(prev[parentId] || []), ...newJobs],
              }))
              setJobsPagination(prev => ({
                ...prev,
                [parentId]: {
                  page: nextPage,
                  hasMore: nextPage < (response?.data?.lastPage || 1),
                  loading: false,
                },
              }))
            }
          } catch (err) {
            setJobsPagination(prev => ({
              ...prev,
              [parentId]: { ...prev[parentId], loading: false },
            }))
          }
        }
      }
    }
  }

  // Update total process loss when selections change or on hover/deselect
  useEffect(() => {
    if (!isEqual(type, 'processLoss')) return
    let total = 0

    const getProcessLossValue = value => {
      if (!value) {
        return 0
      }
      const numValue = parseFloat(value)
      return isNaN(numValue) ? 0 : numValue
    }

    // Helper function to sum processLoss from a job list
    const allJobTypes = [
      'PART_PRODUCTION',
      'ASSEMBLY',
      'PAINTING',
      'MACHINING',
      'FORGING',
      'BLANKING',
      'COMPOUNDING_PELLETIZING',
      'MATERIAL_SALES_MATERIAL_PRODUCER',
      'PRE_COMPOUNDING_PELLETIZING',
      'GRINDING',
      'SORTING',
      'MATERIAL_PRODUCTION',
      'POST_PROCESSING',
      'RECOVERY',
    ]

    const sumProcessLossFromJobs = jobs => {
      if (!Array.isArray(jobs)) return 0
      return jobs.reduce((sum, job) => {
        return sum + getProcessLossValue(job?.outputScrapDtos?.[0]?.processLoss)
      }, 0)
    }

    const allJobsArrays = values(allJobsList)?.flat() || []
    total = allJobTypes.reduce((sum, jobType) => {
      const matchedJobs = allJobsArrays.filter(job =>
        isEqual(job?.jobType, jobType),
      )
      const jobTypeSum = sumProcessLossFromJobs(matchedJobs)
      return sum + jobTypeSum
    }, 0)

    if (selectedItems?.PART_SALES_PRODUCER) {
      const productionLoss =
        partProductionJob[selectedItems?.partList]?.[0]?.outputScrapDtos?.[0]
          ?.processLoss
      total += productionLoss || 0
    }

    total = total ? total?.toFixed(2) : 0

    setTotalProcessLoss(total)
  }, [selectedItems, partProductionJob, allJobsList, type])

  const Sentinel = ({ onVisible }) => {
    const elementRef = useRef(null)

    useEffect(() => {
      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            onVisible()
          }
        },
        { threshold: 0.1 },
      )

      if (elementRef.current) {
        observer.observe(elementRef.current)
      }

      return () => {
        if (elementRef.current) {
          observer.unobserve(elementRef.current)
        }
      }
    }, [onVisible])

    return (
      <div
        ref={elementRef}
        style={{
          height: '40px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Optional: Add a small spinner here if needed, but the main loader might be enough */}
      </div>
    )
  }

  // Get data for each level based on selections
  const getColumnData = level => {
    if (dynamicJobLevels.includes(level)) {
      const dynamicIndex = dynamicJobLevels.indexOf(level)
      const parentLevel =
        dynamicIndex === 0
          ? fixedJobLevels[fixedJobLevels.length - 1]
          : dynamicJobLevels[dynamicIndex - 1]
      const parentJobId = getParentJobId(level)

      if (!parentLevel || !selectedItems[parentLevel] || !parentJobId) {
        return []
      }

      return mapJobsToCards({
        jobs: getJobsForLevel(level, parentJobId),
        jobType: level,
        level,
      })
    }

    switch (level) {
      case 'vehicleDetails':
        return [
          {
            id: level,
            content: renderKeyValueFields(getVehicleFields(vehicleData, type)),
            level,
            title: 'job_VehicleDetails',
          },
        ]

      case 'materialList':
        if (!selectedItems.vehicleDetails) return []

        return materials.map(material => {
          const partsForMaterialInfo = partsByMaterial[material?.id] || []
          return {
            id: material.id,
            content: (
              <div>
                <div>
                  {t('job_RecycledMaterialPercentage')}:{' '}
                  {isEqual(material?.materialTypeKey, 'job_Steel') &&
                  isEqual(partsForMaterialInfo?.[0]?.id, 86260)
                    ? 73.54
                    : partsForMaterialInfo?.[0]?.recycledMaterialUsed ||
                      partsForMaterialInfo?.[0]?.recyclability ||
                      0}
                </div>
              </div>
            ),
            level,
            title: `Material: ${t(material.name)}`,
          }
        })

      case 'partList':
        if (!selectedItems.materialList) return []
        const partsForMaterial =
          partsByMaterial[selectedItems.materialList] || []

        return partsForMaterial.map(part => ({
          id: part.id,
          content: renderKeyValueFields(getPartFields(part)),
          level,
          title: `Part: ${t(part.typeOfProductKey)}`,
        }))

      case 'PART_SALES_PRODUCER':
        if (!selectedItems.partList) return []
        return mapJobsToCards({
          jobs: partProducersByPart[selectedItems.partList] || [],
          jobType: 'PART_SALES_PRODUCER',
          level,
        })

      case 'PART_PRODUCTION':
        if (!selectedItems.PART_SALES_PRODUCER) return []
        return mapJobsToCards({
          jobs:
            partProductionJob[selectedItems.partList] ||
            getJobsForLevel(
              'PART_PRODUCTION',
              selectedItems.PART_SALES_PRODUCER,
            ) ||
            [],
          jobType: 'PART_PRODUCTION',
          level,
        })

      case 'MATERIAL_SALES_PART_PRODUCER':
        if (
          !selectedItems.PART_PRODUCTION &&
          !selectedItems.PART_SALES_PRODUCER
        )
          return []
        return mapJobsToCards({
          jobs: getJobsForLevel(
            'MATERIAL_SALES_PART_PRODUCER',
            selectedItems.PART_PRODUCTION || selectedItems.PART_SALES_PRODUCER,
          ),
          jobType: 'MATERIAL_SALES_PART_PRODUCER',
          level,
        })

      default:
        return []
    }
  }

  const renderCard = ({
    content,
    isActive,
    hasChildren,
    title,
    onClick,
    enableHover = false,
  }) => {
    const handleIconClick = e => {
      e.stopPropagation()
      onClick(e)
    }

    const handleHeaderHover = e => {
      e.stopPropagation()
      // Only trigger if not already active to avoid unnecessary re-renders
      if (!isActive) {
        onClick(e)
      }
    }
    const handleHeaderClick = e => {
      e.stopPropagation()
      onClick(e)
    }

    return (
      <ANTDCard
        className={`traceability-card ${isActive ? 'active-card' : ''}`}
        onMouseEnter={enableHover ? handleHeaderHover : undefined}
        title={
          title ? (
            <div
              onClick={handleHeaderClick}
              style={{
                cursor: 'pointer',
                width: '100%',
              }}
            >
              {t(title)}
            </div>
          ) : undefined
        }
        extra={
          hasChildren && (
            <div onClick={handleIconClick} className="icon-container">
              {isActive ? <MinusOutlined /> : <PlusOutlined />}
            </div>
          )
        }
      >
        <div>{content}</div>
      </ANTDCard>
    )
  }

  const TreeNode = ({ level, id, isRoot = false }) => {
    const items = getColumnData(level)
    const currentItem = items.find(item => item.id === id)

    // If we are at the root, we render all items for that level
    // If we are not at the root, we only render the specific item that was passed
    const itemsToRender = isRoot ? items : currentItem ? [currentItem] : []

    return (
      <>
        {itemsToRender.map(item => {
          const isSelected = selectedItems[item.level] === item.id
          const nextLevelIndex = levelOrder.indexOf(item.level) + 1
          const nextLevel = levelOrder[nextLevelIndex]
          const hasChildren = nextLevel && isSelected

          return (
            <div
              key={item.id}
              className={`tree-node-wrapper ${isRoot ? 'root-node' : ''}`}
            >
              <div className="tree-node-content">
                {renderCard({
                  content: item.content,
                  level: item.level,
                  id: item.id,
                  isActive: isSelected,
                  hasChildren: nextLevelIndex < levelOrder.length,
                  title: item.title,
                  onClick: e => handleCardClick(item.level, item.id, e),
                  enableHover: true,
                })}
              </div>
              {hasChildren && (
                <div className="node-children">
                  {getColumnData(nextLevel).map(child => (
                    <TreeNode
                      key={child.id}
                      level={nextLevel}
                      id={child.id}
                      isRoot={false}
                    />
                  ))}
                  {jobsPagination[item.id]?.hasMore && (
                    <Sentinel onVisible={() => fetchMoreJobs(item.id)} />
                  )}
                  {jobsPagination[item.id]?.loading && (
                    <div style={{ padding: '10px', textAlign: 'center' }}>
                      <ANTDSpin size="small" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </>
    )
  }

  // Render horizontal view
  const renderHorizontalView = () => {
    return (
      <div
        style={{
          display: 'flex',
          overflowX: 'auto',
          width: '100%',
          gap: '16px',
          ...(loading ? { opacity: 0.5 } : {}),
        }}
      >
        {levelOrder.map(columnLevel => (
          <div
            key={columnLevel}
            onScroll={e => handleScroll(e, columnLevel)}
            className={`traceability-column ${
              columnLevel === 'RECOVERY' ? 'elv-column' : ''
            }`}
            style={{
              marginTop: columnOffsets[columnLevel]
                ? `${columnOffsets[columnLevel]}px`
                : '0px',
              maxHeight: `calc(100vh - 100px - ${
                columnOffsets[columnLevel] || 0
              }px)`,
            }}
          >
            {getColumnData(columnLevel).map(item => {
              const isLastLevel =
                levelOrder.indexOf(item.level) === levelOrder.length - 1
              const isElvCard = item?.level === 'RECOVERY'
              return (
                <div
                  key={item.id}
                  data-trace-level={item.level}
                  data-trace-id={item.id}
                  style={{
                    marginBottom: '16px',
                    ...(isElvCard && { marginRight: '16px' }),
                  }}
                >
                  {renderCard({
                    content: item.content,
                    level: item.level,
                    id: item.id,
                    isActive: selectedItems[item.level] === item.id,
                    hasChildren: !isLastLevel,
                    title: item.title,
                    onClick: e => handleCardClick(item.level, item.id, e),
                    enableHover: true,
                  })}
                </div>
              )
            })}
            {columnLevel === 'RECOVERY' &&
              jobsPagination[selectedItems.RECYCLE]?.loading && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '10px',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <ANTDSpin />
                </div>
              )}
          </div>
        ))}
        {loading && (
          <div className="loader-container">
            <ANTDSpin size="large" />
          </div>
        )}
      </div>
    )
  }

  // Render vertical view
  const renderVerticalView = () => {
    return (
      <div className="tree-container">
        {/* Render the root level (vehicleDetails) */}
        <TreeNode level={levelOrder[0]} isRoot={true} />
        {loading && (
          <div className="loader-container">
            <ANTDSpin size="large" />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="vehicle-traceability-container">
      <div
        className={`${
          isEqual(type, 'processLoss') ? 'd-flex space-between' : ''
        }`}
      >
        {isEqual(type, 'processLoss') && (
          <span>
            <strong>{t('inv_TotalProcessLoss')}</strong>: {totalProcessLoss}
          </span>
        )}
        <div className="button-container">
          <ANTDButton
            type={viewType === 'horizontal' ? 'primary' : 'default'}
            className={'primary-color'}
            icon={<AppstoreOutlined />}
            onClick={() => setViewType('horizontal')}
          >
            {t('Horizontal View') || 'Horizontal View'}
          </ANTDButton>
          <ANTDButton
            type={viewType === 'vertical' ? 'primary' : 'default'}
            className={'primary-color'}
            icon={<MenuOutlined />}
            onClick={() => setViewType('vertical')}
          >
            {t('Vertical View') || 'Vertical View'}
          </ANTDButton>
        </div>
      </div>
      <div className="viewTraceability mt-10">
        {viewType === 'horizontal'
          ? renderHorizontalView()
          : renderVerticalView()}
      </div>
    </div>
  )
}

export default VehicleTraceability
